import { Buffer } from 'buffer';

import { isElectron } from '@actual-app/core/shared/environment';
import type { AttachmentMimeType } from '@actual-app/core/types/models';

import type { AttachmentMutationFile } from './mutations';

export const ATTACHMENT_FILE_ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,application/pdf';

type AttachmentSaveRequest = {
  contents: Uint8Array;
  filename: string;
  mimeType: AttachmentMimeType;
};

type AttachmentDownloadAnchor = {
  href: string;
  download: string;
  rel: string;
  style: { display: string };
  click: () => void;
  remove: () => void;
};

export type AttachmentFileEnvironment = {
  isElectron: () => boolean;
  saveFile: (
    contents: Buffer,
    filename: string,
    title: string,
  ) => Promise<void>;
  createObjectURL: (blob: Blob) => string;
  revokeObjectURL: (url: string) => void;
  createAnchor: () => AttachmentDownloadAnchor;
  queueCleanup: (cleanup: () => void) => void;
};

const defaultFileEnvironment: AttachmentFileEnvironment = {
  isElectron,
  saveFile: (contents, filename, title) =>
    window.Actual.saveFile(contents, filename, title),
  createObjectURL: blob => URL.createObjectURL(blob),
  revokeObjectURL: url => URL.revokeObjectURL(url),
  createAnchor: () => document.createElement('a'),
  queueCleanup: cleanup => queueMicrotask(cleanup),
};

export async function readAttachmentFile(
  file: File,
): Promise<AttachmentMutationFile> {
  return {
    name: file.name,
    type: file.type,
    contents: await file.arrayBuffer(),
  };
}

export function formatAttachmentBytes(byteSize: number): string {
  if (byteSize < 1_024) {
    return `${byteSize} B`;
  }

  const kilobytes = byteSize / 1_024;
  if (kilobytes < 1_024) {
    return `${Number(kilobytes.toFixed(1))} KB`;
  }

  return `${Number((kilobytes / 1_024).toFixed(1))} MB`;
}

export async function saveAttachmentFile(
  request: AttachmentSaveRequest,
  environment: AttachmentFileEnvironment = defaultFileEnvironment,
): Promise<void> {
  if (environment.isElectron()) {
    await environment.saveFile(
      Buffer.from(request.contents),
      request.filename,
      'Save attachment',
    );
    return;
  }

  const blob = new Blob([request.contents.slice().buffer], {
    type: request.mimeType,
  });
  const url = environment.createObjectURL(blob);
  const anchor = environment.createAnchor();
  anchor.href = url;
  anchor.download = request.filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  anchor.click();

  environment.queueCleanup(() => {
    anchor.remove();
    environment.revokeObjectURL(url);
  });
}
